import httpClient from './httpClient';
import { buildQueryString } from '../utils/queryBuilder';
import { parseApiError } from '../utils/errorHandler';

const ENDPOINT = '/api/responsavel-recebimento';

/**
 * Normaliza resposta paginada (HAL ou content direto) para o formato padrão do projeto.
 * @private
 */
function normalizePageResponse(json, fallbackPage) {
    const embedded = json._embedded;
    const content = embedded
        ? (embedded.responsibleReceipt ?? embedded.responsibles ?? embedded.content ?? Object.values(embedded)[0] ?? [])
        : (json.content ?? []);
    const list = Array.isArray(content) ? content : [];
    const pageMeta = json.page || {};
    const totalPages = pageMeta.totalPages ?? 0;
    const pageNumber = Math.max(0, pageMeta.number ?? fallbackPage ?? 0);
    return {
        content: list,
        page: {
            number: pageNumber,
            totalPages,
            first: totalPages === 0 || pageNumber === 0,
            last: totalPages === 0 || pageNumber >= totalPages - 1,
        },
    };
}

/**
 * Lista responsáveis pelo recebimento (padrão das demais consultas do projeto).
 * GET /api/responsavel-recebimento
 * @param {Object} params
 * @param {number} [params.page=0]
 * @param {number} [params.size=10]
 * @param {string} [params.sort=name,asc]
 * @param {string|number|null} [params.customerId]
 * @param {boolean|string} [params.active] - true/false ou 'true'/'false'
 * @returns {Promise<{ content: Array, page: { number, totalPages, first, last } }>}
 */
async function list({ page = 0, size = 10, sort = 'name,asc', customerId, active } = {}) {
    const filters = {};
    if (customerId != null && String(customerId).trim() !== '') {
        filters.customerId = String(customerId);
    }
    if (active !== undefined && active !== null && active !== '') {
        filters.active = active === 'true' || active === true;
    } else {
        filters.active = true;
    }
    const queryString = buildQueryString({ page, size, sort: sort || 'name,asc', filters });
    const response = await httpClient.get(`${ENDPOINT}?${queryString}`);
    if (!response.ok) {
        throw await parseApiError(response);
    }
    const json = await response.json();
    return normalizePageResponse(json, page);
}

/**
 * POST /api/responsavel-recebimento
 * CreateResponsibleReceiptRequest: name (2-100), document?, phoneNumber?, customerId?, active (tela envia true).
 * @param {Object} body - { name, document?, phoneNumber? }
 * @param {string|number|null} [customerId] - somente empresa de transporte; enviado no body quando informado
 */
async function create(body, customerId) {
    const name = (body?.name ?? '').trim();
    if (name.length < 2) {
        throw new Error('Nome deve ter entre 2 e 100 caracteres.');
    }
    const payload = {
        name: name.slice(0, 100),
        document: body?.document ? String(body.document).replace(/\D/g, '') || null : null,
        phoneNumber: body?.phoneNumber != null ? String(body.phoneNumber).trim() || null : null,
        active: true,
    };
    if (customerId != null && String(customerId).trim() !== '') {
        payload.customerId = Number(customerId);
    }
    const response = await httpClient.post(ENDPOINT, payload);
    if (!response.ok) {
        throw await parseApiError(response);
    }
    const location = response.headers.get('Location');
    const idFromLocation = location ? location.replace(/.*\//, '').replace(/\?.*$/, '').trim() : null;
    return { id: idFromLocation != null ? Number(idFromLocation) : null, name: payload.name };
}

/**
 * PUT /api/responsavel-recebimento
 * UpdateResponsibleReceiptRequest: id, name?, phoneNumber?, customerId?, active?
 * Somente empresa de transporte envia customerId.
 * @param {Object} body - { id, name?, phoneNumber?, customerId?, active? }
 */
async function update(body) {
    const id = body?.id;
    if (id == null) {
        throw new Error('ID é obrigatório para atualização.');
    }
    const payload = {
        id: Number(id),
    };
    if (body?.name !== undefined) payload.name = body.name != null ? String(body.name).trim() : null;
    if (body?.phoneNumber !== undefined) payload.phoneNumber = body.phoneNumber != null ? String(body.phoneNumber).trim() || null : null;
    if (body?.active !== undefined) payload.active = Boolean(body.active);
    if (body?.customerId != null && String(body.customerId).trim() !== '') {
        payload.customerId = Number(body.customerId);
    }
    const response = await httpClient.put(ENDPOINT, payload);
    if (!response.ok) {
        throw await parseApiError(response);
    }
    return { id: payload.id };
}

const responsibleReceiptService = {
    list,
    create,
    update,
};

export default responsibleReceiptService;
